package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.manager.LumaticaApiSettings
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.DashboardVerlaufHintergrund
import org.example.project.utils.FormStyles
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

enum class ZweiFaktorRecoveryBereich(val api: String) {
    SYS("zweifaktor"),
    NET("desktopregistrierungscreen")
}

private val zweiFaktorRecoveryJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun recoveryParam(name: String): String {
    val sources = listOf(
        window.location.search.substringAfter("?", ""),
        window.location.search.removePrefix("?")
    )
    return sources.asSequence()
        .flatMap { it.split("&").asSequence() }
        .mapNotNull {
            val key = it.substringBefore("=")
            val value = it.substringAfter("=", "")
            if (key == name) js("decodeURIComponent")(value) as String else null
        }
        .firstOrNull()
        .orEmpty()
}

private suspend fun recoveryPost(api: String, action: String, body: JsonObject): JsonObject? {
    val response = window.fetch(
        "${LumaticaApiSettings.getBaseUrl()}/api/$api/$action",
        RequestInit(
            method = "POST",
            headers = Headers().apply { append("Content-Type", "application/json") },
            body = zweiFaktorRecoveryJson.encodeToString(body)
        )
    ).await()
    if (!response.ok) return null
    return runCatching { zweiFaktorRecoveryJson.parseToJsonElement(response.text().await()).jsonObject }.getOrNull()
}

@Composable
fun ZweiFaktorRecoveryScreen(
    bereich: ZweiFaktorRecoveryBereich,
    onComplete: () -> Unit
) {
    Style(cssRules = FormStyles.cssRules)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    val token = remember { recoveryParam("zweiFaktorReset") }
    var linkGeprueft by remember { mutableStateOf(false) }
    var linkGueltig by remember { mutableStateOf(false) }
    var password by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var setupToken by remember { mutableStateOf("") }
    var qrCode by remember { mutableStateOf("") }
    var setupKey by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }

    LaunchedEffect(token, bereich) {
        if (token.isBlank()) {
            linkGeprueft = true
            message = "Der Zwei-Faktor-Wiederherstellungslink fehlt."
            return@LaunchedEffect
        }
        val result = recoveryPost(
            bereich.api,
            if (bereich == ZweiFaktorRecoveryBereich.SYS) "reset-pruefen" else "zwei-faktor-reset-pruefen",
            buildJsonObject { put("token", JsonPrimitive(token)) }
        )
        linkGueltig = result?.get("ok")?.jsonPrimitive?.contentOrNull == "true"
        linkGeprueft = true
        if (!linkGueltig) message = "Dieser Wiederherstellungslink ist ungültig oder der Zugang ist nicht mehr wegen Zwei-Faktor gesperrt."
    }

    fun startRecovery() {
        if (loading || !linkGueltig || password.isBlank()) return
        loading = true
        message = ""
        scope.launch {
            try {
                val result = recoveryPost(
                    bereich.api,
                    if (bereich == ZweiFaktorRecoveryBereich.SYS) "reset-starten" else "zwei-faktor-reset-starten",
                    buildJsonObject {
                        put("token", JsonPrimitive(token))
                        put("password", JsonPrimitive(password))
                    }
                )
                if (result?.get("ok")?.jsonPrimitive?.contentOrNull != "true") {
                    message = "Passwort falsch oder der Zugang ist nicht mehr wegen Zwei-Faktor gesperrt."
                    return@launch
                }
                setupToken = result["zweiFaktorToken"]?.jsonPrimitive?.contentOrNull.orEmpty()
                qrCode = result["zweiFaktorQrCode"]?.jsonPrimitive?.contentOrNull.orEmpty()
                setupKey = result["zweiFaktorSetupKey"]?.jsonPrimitive?.contentOrNull.orEmpty()
                code = ""
                password = ""
            } finally {
                loading = false
            }
        }
    }

    fun completeRecovery() {
        if (loading || setupToken.isBlank() || code.length != 6) return
        loading = true
        message = ""
        scope.launch {
            try {
                val result = recoveryPost(
                    bereich.api,
                    if (bereich == ZweiFaktorRecoveryBereich.SYS) "reset-ausfuehren" else "zwei-faktor-reset-ausfuehren",
                    buildJsonObject {
                        put("token", JsonPrimitive(token))
                        put("zweiFaktorToken", JsonPrimitive(setupToken))
                        put("code", JsonPrimitive(code))
                    }
                )
                if (result?.get("ok")?.jsonPrimitive?.contentOrNull != "true") {
                    message = "Code falsch oder Wiederherstellung abgelaufen."
                    return@launch
                }
                message = "Zwei-Faktor-Zugang wurde wiederhergestellt. Bitte melde dich jetzt normal an."
                onComplete()
            } finally {
                loading = false
            }
        }
    }

    Div({
        classes(FormStyles.wrapper)
        style {
            property("position", "relative")
            property("z-index", "1")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(500.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(20.px)
                padding(32.px)
                boxSizing("border-box")
            }
        }) {
            H1({ classes(FormStyles.h1) }) { Text("Zwei-Faktor wiederherstellen") }
            if (!linkGeprueft) {
                P({ classes(FormStyles.h3) }) { Text("Link wird geprüft...") }
            } else if (!linkGueltig) {
                P({ classes(FormStyles.h3) }) { Text(message) }
            } else if (setupToken.isBlank()) {
                Input(InputType.Password, attrs = {
                    classes(FormStyles.input)
                    if (loading) disabled()
                    placeholder("Passwort bestätigen")
                    value(password)
                    onInput { password = it.value; message = "" }
                    onKeyDown {
                        if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                            it.preventDefault()
                            startRecovery()
                        }
                    }
                })
                message.takeIf { it.isNotBlank() }?.let { P({ classes(FormStyles.h3) }) { Text(it) } }
                Div({ style { width(100.percent); marginTop(8.px) } }) {
                    LumaticaButton(
                        label = if (loading) "..." else "2FA-Wiederherstellung starten",
                        onClick = ::startRecovery,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !loading,
                        fillWidth = true,
                        heightPx = 44
                    )
                }
            } else {
                if (qrCode.isNotBlank()) {
                    Div({
                        style {
                            width(100.percent)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            property("justify-content", "center")
                        }
                    }) {
                        Img(src = qrCode, attrs = {
                            style { width(220.px) }
                        })
                    }
                }
                if (setupKey.isNotBlank()) {
                    Input(InputType.Text, attrs = {
                        classes(FormStyles.input)
                        attr("readonly", "readonly")
                        value(setupKey)
                        onClick { it.target.asDynamic().select() }
                    })
                }
                Input(InputType.Text, attrs = {
                    classes(FormStyles.input)
                    if (loading) disabled()
                    placeholder("6-stelliger Code")
                    value(code)
                    attr("inputmode", "numeric")
                    attr("autocomplete", "one-time-code")
                    onInput {
                        code = it.value.filter { char -> char in '0'..'9' }.take(6)
                        message = ""
                    }
                    onKeyDown {
                        if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                            it.preventDefault()
                            completeRecovery()
                        }
                    }
                })
                message.takeIf { it.isNotBlank() }?.let { P({ classes(FormStyles.h3) }) { Text(it) } }
                Div({ style { width(100.percent); marginTop(8.px) } }) {
                    LumaticaButton(
                        label = if (loading) "..." else "2FA wiederherstellen",
                        onClick = ::completeRecovery,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !loading,
                        fillWidth = true,
                        heightPx = 44
                    )
                }
            }
        }
    }
}
